import { atom, selector, selectorFamily } from 'recoil'
import { Insight } from 'src/components/insights/types'
import { Demographics } from 'src/components/insights/demographics'
import { CustomPolygonCategoryRank } from 'src/components/insights/custom-polygon-category-rank'
import { CustomPolygonVisits } from 'src/components/insights/custom-polygon-visits'
import { CustomPolygonTTA } from 'src/components/insights/custom-polygon-tta'
import { AreaVisits } from 'src/components/insights/area-visits'
import { DriveTimeTradeArea } from 'src/components/insights/drive-time-trade-area'
import { DEFAULT_LRU_SIZE, keyFactory, uncachedSelector } from 'src/utils/recoil'
import { SearchResultType, googlePlace, selectedLocation } from 'src/atoms/search'
import { PropertyRecords } from 'src/components/insights/property-records'
import { Photos } from 'src/components/insights/photos'
import { CarTraffic } from 'src/components/insights/car-traffic'
import { RadiusRingsTradeArea } from 'src/components/insights/radius-rings-trade-area'
import { Zoning } from 'src/components/insights/zoning'
import { featureEnabled } from 'src/atoms/admin'
import { hasCustomArea } from 'src/atoms/custom-area'
import { Mobility } from 'src/components/insights/location-visits'
import { TrueTradeArea } from 'src/components/insights/true-trade-area'
import { Rank } from 'src/components/insights/rank'
import { CategoryRank } from 'src/components/insights/category-rank'
import { TableFormat } from 'src/components/insights/true-trade-area/overview/constants'
import { AMLocation, CommonTradeAreaSection, DemoRadiiResponse, DemographicDataPoint } from 'src/common-services'
import { alphamapClient } from 'src/clients'
import { computeHispanicPercentage } from 'src/atoms/demographics'
import { shoppingCenter } from 'src/atoms/mobility/tenant-overview'
import { TenantOverview } from 'src/components/insights/tenant-overview'
import { Info } from '@emotion-icons/fa-solid/Info'
import { Colors } from 'src/components/theme'
import type { DemoTableOptions } from 'src/components/demo-table/table/toolbar'
import { ChartData } from 'src/atoms/buildout'
import { formatChartData } from 'src/components/demo-table/utils'
import { Unavailable } from 'src/components/insights/unavailable'
import { InsightBuilder } from 'src/views/insight-builder'
import { OwnerSearchLegacy } from 'src/components/insights/owner-search-legacy'
import { PlaceDebug } from 'src/components/insights/place-debug'
import { IS_PROD } from 'src/config'
import { NationalAvgDataType } from 'src/utils/demographics'
import { OwnerSearch } from 'src/components/insights/owner-search'
import { BrandProfile } from 'src/components/insights/brand-profile'
import { NoBrandRankInsight } from 'src/components/insights/performance-benchmarks/no-rank'
import * as ttaOverviewAtoms from 'src/atoms/true-trade-area/overview'

const key = keyFactory('insights')

export const currentTrueTradeAreaDataPoint = atom<DemographicDataPoint>({
  key: key('tta-current-data-point'),
  default: DemographicDataPoint.Overview,
})

export type TrueTradeAreaTableView = 'charts' | 'table'

export const trueTradeAreaTableView = atom<TrueTradeAreaTableView>({
  key: key('tta-table-view'),
  default: 'charts',
})

export const trueTradeAreaTableOptions = atom<DemoTableOptions>({
  key: key('tta-table-options'),
  default: {
    showCharts: true,
    showFiveYearForecast: true,
    showNationalAvg: false,
    showCounts: false,
  },
})

export const ttareaComparedemographicsData = uncachedSelector<CommonTradeAreaSection[] | undefined>({
  key: key('ttarea-compare-demographics-data'),
  get: async ({ get }) => {
    const location = get(selectedLocation)
    if (!location) {
      return undefined
    }

    const compareLocations = get(ttaOverviewAtoms.compareLocations)

    const start = get(ttaOverviewAtoms.startDate)
    const end = get(ttaOverviewAtoms.endDate)
    if (!start || !end) {
      return undefined
    }

    const type = get(ttaOverviewAtoms.boundaryType)
    const _visitorOrigin = get(ttaOverviewAtoms.visitorOrigin)
    const percentFilter = get(ttaOverviewAtoms.demoDataPercentageFilter)

    const promises = []

    promises.push(
      alphamapClient.demographics.getDemographicsLocationByVisitorsHome({
        id: location.id,
        startDate: start,
        endDate: end,
        visitorOrigin: _visitorOrigin,
        type,
        percentageFilter: percentFilter,
      })
    )

    for (const compareLocation of compareLocations) {
      promises.push(
        alphamapClient.demographics.getDemographicsLocationByVisitorsHome({
          id: compareLocation.id,
          startDate: start,
          endDate: end,
          visitorOrigin: _visitorOrigin,
          type,
          percentageFilter: percentFilter,
        })
      )
    }

    const responses = await Promise.all(promises)

    return responses.map((response) => ({
      ...response,
      demographics: computeHispanicPercentage(response.demographics),
    }))
  },
  lru: DEFAULT_LRU_SIZE,
})

export const ttareademographicsData = uncachedSelector<CommonTradeAreaSection | undefined>({
  key: key('ttarea-demographics-data'),
  get: ({ get }) => {
    const data = get(ttareaComparedemographicsData) || []
    const [locationData] = data
    return locationData
  },
})

export const ttareaCompareDemographicsChartData = selectorFamily<
  Array<ChartData>,
  { dataPoint: string; percentUnit: number[]; format: TableFormat; dataType?: NationalAvgDataType }
>({
  key: key('ttarea-compare-demographics-chart-data'),
  get:
    ({ dataPoint, percentUnit, format, dataType }) =>
    ({ get }) => {
      const data = get(ttareaComparedemographicsData) || []
      const tableOptions = get(trueTradeAreaTableOptions)
      const area = get(ttaOverviewAtoms.shapesCount)
      return data.map((datum, index) => {
        const dp = datum?.demographics[dataPoint]
        if (!dp) {
          return {}
        }

        return formatChartData({
          currentDataPoint: dataPoint,
          dataPoint: dp,
          tableOptions,
          percentUnit: percentUnit[index],
          format,
          area,
          dataType,
        })
      })
    },
})

export const ttareaDemographicsChartData = selectorFamily<
  ChartData[],
  { dataPoint: string; percentUnit: number[]; format: TableFormat }
>({
  key: key('ttarea-demographics-chart-data'),
  get:
    ({ dataPoint, percentUnit, format }) =>
    ({ get }) => {
      const data = get(ttareaCompareDemographicsChartData({ dataPoint, percentUnit, format })) || []
      const [primaryLocationData] = data
      return [primaryLocationData]
    },
})

export const ttareaDemographicsWidgetData = selector<DemoRadiiResponse>({
  key: key('ttarea-demographics-widget-data'),
  get: async ({ get }) => {
    const ttareademographicsDataValue = get(ttareademographicsData)
    if (!ttareademographicsDataValue) {
      return []
    }
    return [
      {
        radius: 1,
        data: ttareademographicsDataValue.demographics,
      },
    ]
  },
})

export const overviewCompareTableColumns = selectorFamily<
  Array<Record<string, Record<string, number>>>,
  { dataPoint: string; format: TableFormat }
>({
  key: key('overview-compare-table-columns'),
  get:
    ({ dataPoint, format }) =>
    ({ get }) => {
      const locationsDemographics = get(ttareaComparedemographicsData) || []
      return locationsDemographics.map((demographics) => {
        const cols: Record<string, Record<string, number>> = {
          currentYear: {},
        }
        for (const col in cols) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { title, showFloatingTabs, dataInPercentages, ...rows } = format
          for (const rowKey of Object.values(rows).flat()) {
            let datum = (demographics?.demographics[dataPoint][rowKey] as Record<string, number>)?.[col]
            if (rowKey === 'Non Hispanic') {
              datum = 100 - (demographics?.demographics[dataPoint]['% Hispanic'] as Record<string, number>)[col]
            }
            cols[col][rowKey] = datum
          }
        }
        return cols
      })
    },
})

export const overviewTableColumns = selectorFamily<
  Record<string, Record<string, number>>,
  { dataPoint: string; format: TableFormat }
>({
  key: key('overview-table-columns'),
  get:
    ({ dataPoint, format }) =>
    ({ get }) => {
      const [primaryLocationColumns] = get(overviewCompareTableColumns({ dataPoint, format })) || []
      return primaryLocationColumns
    },
})

const insights = selector<Insight[]>({
  key: key('all'),
  get: ({ get }) => {
    const isOwnerSearchEnabled = get(featureEnabled('owner-search'))
    const isBrandProfileEnabled = get(featureEnabled('brand-profile'))
    const availableShoppingCenter = get(shoppingCenter)
    const ShoppingCenter = availableShoppingCenter
      ? TenantOverview
      : {
          ...TenantOverview,
          nameIcon: Info,
          nameIconColor: Colors.neutral400,
        }

    const allInsights = [
      InsightBuilder,
      PropertyRecords,
      isOwnerSearchEnabled ? OwnerSearch : false,
      isOwnerSearchEnabled ? OwnerSearchLegacy : false,
      Zoning,
      Demographics,
      CarTraffic,
      RadiusRingsTradeArea,
      DriveTimeTradeArea,
      CustomPolygonCategoryRank,
      CustomPolygonTTA,
      CustomPolygonVisits,
      AreaVisits,
      Photos,
      Mobility,
      TrueTradeArea,
      Rank,
      NoBrandRankInsight,
      CategoryRank,
      ShoppingCenter,
      isBrandProfileEnabled ? BrandProfile : false,
      Unavailable,
      IS_PROD ? undefined : PlaceDebug,
    ]
    // typescript doesn't understand filter(Boolean) to remove falsy values
    return allInsights.filter(Boolean) as Insight[]
  },
})

export function isBranded(location: AMLocation | undefined): boolean {
  return Boolean(location?.brandId)
}

export const searchResultType = selector<SearchResultType | undefined>({
  key: key('search-result-type'),
  get: ({ get }) => {
    const location = get(selectedLocation)
    if (location) {
      return 'alphamap-location'
    }
    if (get(googlePlace)) {
      return 'address'
    }
    if (get(hasCustomArea)) {
      return 'area'
    }
    return undefined
  },
})

export const hasRankInsight = selector({
  key: key('has-rank-insight'),
  get: ({ get }) => {
    const location = get(selectedLocation)
    if (!location) {
      return false
    }

    return location.hasMobility && location.brandId
  },
})

export const availableInsights = selector<Insight[]>({
  key: key('available-insights'),
  get: ({ get }) => {
    const type = get(searchResultType)
    if (!type) {
      return []
    }

    const all = get(insights)
    if (all.length === 0) {
      return []
    }

    let filtered = all.filter((insight) => insight.supportedResultTypes.includes(type))

    // Depending on if the location is an alphamap-location, we may need to replace pre-vetted insights with non-vetted insights or vice versa
    let hasMobility = get(selectedLocation)?.hasMobility
    hasMobility = hasMobility === undefined ? false : hasMobility // default to false if undefined because of cache
    if (hasMobility) {
      // Remove non-vetted insights
      filtered = filtered.filter(
        (x) => x.id !== CustomPolygonTTA.id && x.id !== CustomPolygonVisits.id && x.id !== CustomPolygonCategoryRank.id
      )
    } else {
      // Remove vetted insights
      filtered = filtered.filter((x) => x.id !== TrueTradeArea.id && x.id !== Mobility.id)
    }

    const hasRank = get(hasRankInsight)
    // remove brand rank if it doesn't have rank and add non-brand rank in its place
    if (!hasRank) {
      filtered = filtered.filter((x) => x.id !== Rank.id && x.id !== CategoryRank.id)
    } else {
      filtered = filtered.filter((x) => x.id !== NoBrandRankInsight.id)
    }

    return filtered.map((x) => {
      return {
        ...x,
        available: true,
        locked: false,
      }
    })
  },
})
